import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, Volume2, VolumeX, Play } from 'lucide-react';
import { useCountry, useCtaHandler } from '../context/CountryContext';
import FadeInSection from './FadeInSection';

const VIDEO_URL = 'https://customer-assets-lxgj4vgw.emergentagent.net/job_trader-mirror-site/artifacts/cgzgvvnl_VIDEO%203%20MBE%20INT.mp4';

export default function WhySilver() {
  const { telegramUrl } = useCountry();
  const track = useCtaHandler();

  const wrapperRef = useRef(null);
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true); // start muted; will try to unmute below
  const [playing, setPlaying] = useState(false);

  // Try to autoplay UNMUTED. Browsers block unmuted autoplay without prior
  // user interaction, so we always fall back to muted autoplay + a big
  // "tap to unmute" hint. On the visitor's first click anywhere on the page,
  // we then attempt to unmute the video automatically.
  useEffect(() => {
    const v = videoRef.current;
    const w = wrapperRef.current;
    if (!v || !w) return;

    v.playsInline = true;

    const tryPlayUnmuted = () => {
      v.muted = false;
      const p = v.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          setMuted(false);
          setPlaying(true);
        }).catch(() => {
          // Browser blocked unmuted autoplay — retry muted
          v.muted = true;
          setMuted(true);
          const q = v.play();
          if (q && typeof q.catch === 'function') q.catch(() => {});
          setPlaying(true);
        });
      }
    };

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            tryPlayUnmuted();
          } else {
            v.pause();
            setPlaying(false);
          }
        });
      },
      { threshold: 0.35 }
    );
    obs.observe(w);

    // On visitor's first user gesture anywhere, try again to unmute
    const unmuteOnGesture = () => {
      if (!v) return;
      if (v.muted) {
        v.muted = false;
        const p = v.play();
        if (p && typeof p.then === 'function') {
          p.then(() => setMuted(false)).catch(() => {});
        } else {
          setMuted(false);
        }
      }
      window.removeEventListener('click', unmuteOnGesture);
      window.removeEventListener('touchstart', unmuteOnGesture);
      window.removeEventListener('keydown', unmuteOnGesture);
    };
    window.addEventListener('click', unmuteOnGesture, { once: true });
    window.addEventListener('touchstart', unmuteOnGesture, { once: true });
    window.addEventListener('keydown', unmuteOnGesture, { once: true });

    return () => {
      obs.disconnect();
      window.removeEventListener('click', unmuteOnGesture);
      window.removeEventListener('touchstart', unmuteOnGesture);
      window.removeEventListener('keydown', unmuteOnGesture);
    };
  }, []);

  function toggleMute() {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (v.paused) {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  }

  return (
    <section id="why-silver" className="relative py-20 md:py-28">
      <div className="absolute inset-x-0 top-10 h-[420px] radial-glow-soft opacity-70 pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-6">
        {/* Tag pill */}
        <FadeInSection className="flex justify-center">
          <div className="liquid-glass rounded-full inline-flex items-center gap-2 px-3.5 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ff7a2f] relative z-[1]" />
            <span className="text-[11.5px] uppercase tracking-[0.18em] text-white/95 relative z-[1]">Straight From Zack</span>
          </div>
        </FadeInSection>

        {/* Heading */}
        <FadeInSection delay={0.1} className="mt-6 text-center max-w-3xl mx-auto">
          <h2 className="font-display text-white text-3xl sm:text-4xl md:text-[52px] leading-[1.05] tracking-tight">
            Why trade <span className="text-orange-gradient">silver</span>, not <span className="text-blue-gradient">gold</span>?
          </h2>
          <p className="mt-5 text-[#f2f2f2] text-[15px]">
            Watch Zack break down the real edge silver has over gold for retail traders looking to stack lots fast and safely.
          </p>
        </FadeInSection>

        {/* Video — 9:16 portrait, centered */}
        <FadeInSection delay={0.15} className="mt-10 flex justify-center">
          <div
            ref={wrapperRef}
            className="relative rounded-[1.5rem] overflow-hidden card-dark w-full max-w-[380px]"
            style={{ aspectRatio: '9/16' }}
          >
            <video
              ref={videoRef}
              src={VIDEO_URL}
              className="absolute inset-0 w-full h-full object-cover"
              playsInline
              autoPlay
              loop
              preload="auto"
              onClick={toggleMute}
            />

            {/* Bottom controls bar */}
            <div className="absolute left-3 right-3 bottom-3 flex items-center justify-between pointer-events-none">
              <div className="pointer-events-auto liquid-glass rounded-full inline-flex items-center gap-2 px-3 py-1.5">
                <span className="relative z-[1] w-1.5 h-1.5 rounded-full bg-[#ff7a2f] animate-pulse" />
                <span className="relative z-[1] text-[10.5px] uppercase tracking-[0.18em] text-white/95">
                  {playing ? 'Playing' : 'Paused'}
                </span>
              </div>
              <button
                onClick={toggleMute}
                className="pointer-events-auto liquid-glass rounded-full inline-flex items-center gap-2 px-3 py-2 text-[11px] text-white uppercase tracking-wide"
                aria-label={muted ? 'Unmute' : 'Mute'}
              >
                {muted ? <VolumeX className="w-4 h-4 relative z-[1]" /> : <Volume2 className="w-4 h-4 relative z-[1]" />}
              </button>
            </div>

            {/* Muted hint overlay - only shown while still muted */}
            {muted && (
              <button
                onClick={toggleMute}
                className="absolute top-3 left-1/2 -translate-x-1/2 liquid-glass rounded-full inline-flex items-center gap-2 px-3.5 py-1.5 text-[10.5px] uppercase tracking-[0.18em] text-white/95"
                aria-label="Unmute video"
              >
                <Play className="w-3 h-3 relative z-[1] fill-white text-white" />
                <span className="relative z-[1]">Tap to hear Zack</span>
              </button>
            )}
          </div>
        </FadeInSection>

        {/* CTA */}
        <FadeInSection delay={0.2} className="mt-10 text-center">
          <a
            href={telegramUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => track('why-silver-cta')}
            className="btn-orange inline-flex items-center gap-2 px-6 py-3.5 text-[14.5px]"
          >
            Start Trading Silver With MBE <ArrowUpRight className="w-4 h-4" />
          </a>
        </FadeInSection>
      </div>
    </section>
  );
}
